import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import SignatureCanvas from 'react-signature-canvas';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheck, Spinner, Warning, Check, CaretDown,
  DownloadSimple, EnvelopeSimple
} from '@phosphor-icons/react';

const SECTION_ANCHORS = [
  { id: 'sec-1', label: '1. Definitions', search: '1. DEFINITIONS' },
  { id: 'sec-2', label: '2. Non-Disclosure', search: '2. NON-DISCLOSURE' },
  { id: 'sec-3', label: '3. Non-Use', search: '3. NON-USE' },
  { id: 'sec-4', label: '4. Non-Circumvention', search: '4. NON-CIRCUMVENTION' },
  { id: 'sec-5', label: '5. IP Ownership', search: '5. INTELLECTUAL PROPERTY' },
  { id: 'sec-6', label: '6. No Copying', search: '6. NO COPYING' },
  { id: 'sec-7', label: '7. Return & Destruction', search: '7. RETURN AND DESTRUCTION' },
  { id: 'sec-8', label: '8. Term & Survival', search: '8. TERM AND SURVIVAL' },
  { id: 'sec-9', label: '9. Remedies', search: '9. REMEDIES' },
  { id: 'sec-12', label: '12. Governing Law', search: '12. GOVERNING LAW' },
  { id: 'sec-13', label: '13. General', search: '13. GENERAL PROVISIONS' },
];

const NDAPage = () => {
  const navigate = useNavigate();
  const sigPadRef = useRef(null);
  const agreementRef = useRef(null);

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [signed, setSigned] = useState(null);

  // Form state
  const [form, setForm] = useState({
    full_legal_name: '',
    email: '',
    company: '',
    role_title: '',
    country: '',
    phone: '',
    checkbox_accepted: false,
    typed_signature_name: '',
  });

  useEffect(() => {
    const fetchAgreement = async () => {
      try {
        const { data } = await axios.get(`${API}/agreements/current`);
        setAgreement(data);
      } catch (err) {
        setError('Failed to load agreement');
      } finally {
        setLoading(false);
      }
    };
    fetchAgreement();
  }, []);

  const handleChange = (field) => (e) => {
    const value = field === 'checkbox_accepted' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  const scrollToSection = (search) => {
    if (!agreementRef.current) return;
    const text = agreementRef.current.innerText;
    const idx = text.indexOf(search);
    if (idx === -1) return;
    // Find the element that contains this text
    const walker = document.createTreeWalker(agreementRef.current, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      if (walker.currentNode.textContent.includes(search)) {
        walker.currentNode.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        break;
      }
    }
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!form.full_legal_name.trim()) { setError('Full legal name is required'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }
    if (!form.country.trim()) { setError('Country is required'); return; }
    if (!form.checkbox_accepted) { setError('You must accept the agreement'); return; }
    if (!form.typed_signature_name.trim()) { setError('Please type your full name as signature'); return; }
    if (form.typed_signature_name.trim().toLowerCase() !== form.full_legal_name.trim().toLowerCase()) {
      setError('Typed signature must match your full legal name exactly');
      return;
    }

    // Get drawn signature — use toDataURL() directly to avoid trim-canvas interop bug
    let drawnSignature = null;
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      try {
        drawnSignature = sigPadRef.current.getCanvas().toDataURL('image/png').split(',')[1];
      } catch (canvasErr) {
        console.warn('Signature capture fallback:', canvasErr);
      }
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/agreements/sign`, {
        ...form,
        drawn_signature_data: drawnSignature,
        agreement_version: agreement.version,
      });
      setSigned(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to sign agreement');
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmation page
  if (signed) {
    const emailConfigured = signed.email_configured;
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-6 py-12" data-testid="nda-confirmation">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-[#235347] rounded-full flex items-center justify-center mb-6">
            <ShieldCheck size={40} className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Agreement Signed</h1>
          <p className="text-slate-600 mb-2">
            Your confidentiality agreement has been executed successfully.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Your signed copy has been generated and stored permanently.
            {emailConfigured
              ? ' A confirmation email will be sent shortly.'
              : ' Email delivery is pending configuration and can be sent later.'}
          </p>

          <div className="bg-white border border-slate-200 rounded-xl p-5 text-left mb-6 space-y-3">
            <div className="flex justify-between">
              <span className="text-xs text-slate-500">Agreement ID</span>
              <span className="text-xs text-slate-700 font-mono">{signed.agreement_id.slice(0, 16)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-slate-500">Document Hash</span>
              <span className="text-xs text-slate-700 font-mono truncate ml-4">{signed.snapshot_hash.slice(0, 20)}...</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-slate-500">Executed</span>
              <span className="text-xs text-slate-700">{new Date(signed.signed_at).toLocaleString()}</span>
            </div>
          </div>

          <a
            href={`${API}/agreements/${signed.agreement_id}/pdf`}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#235347] text-white rounded-xl font-semibold hover:bg-[#1A3F36] transition-colors mb-4"
            data-testid="download-signed-pdf"
          >
            <DownloadSimple size={20} /> Download Your Signed Agreement
          </a>

          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Return to Kindra
          </button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Spinner size={32} className="animate-spin text-[#235347]" />
      </div>
    );
  }

  // Format agreement text into styled HTML
  const formatAgreement = (text) => {
    if (!text) return '';
    return text
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        if (!trimmed) return '<div class="h-3"></div>';
        // Main title
        if (trimmed === 'CONFIDENTIALITY AND NON-DISCLOSURE AGREEMENT') {
          return `<h1 class="text-xl font-semibold text-[#235347] text-center mb-6">${trimmed}</h1>`;
        }
        // Section headers (numbered)
        if (/^\d+\.\s[A-Z\s&/]+$/.test(trimmed)) {
          return `<h2 class="text-sm font-bold text-slate-900 mt-8 mb-3 uppercase tracking-wide">${trimmed}</h2>`;
        }
        // Subsections
        if (/^\d+\.\d+\s/.test(trimmed)) {
          return `<p class="text-sm text-slate-700 leading-relaxed mb-2 pl-4">${trimmed}</p>`;
        }
        // Lettered sub-items
        if (/^\([a-z]\)\s/.test(trimmed)) {
          return `<p class="text-sm text-slate-600 leading-relaxed mb-1.5 pl-8">${trimmed}</p>`;
        }
        // BETWEEN / AND / RECITALS headers
        if (/^(BETWEEN|AND|RECITALS|NOW THE PARTIES)/.test(trimmed)) {
          return `<p class="text-sm font-semibold text-slate-800 mt-4 mb-2">${trimmed}</p>`;
        }
        // Lettered recitals
        if (/^[A-C]\.\s/.test(trimmed)) {
          return `<p class="text-sm text-slate-700 leading-relaxed mb-2 pl-4">${trimmed}</p>`;
        }
        return `<p class="text-sm text-slate-700 leading-relaxed mb-2">${trimmed}</p>`;
      })
      .join('');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="nda-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck size={24} className="text-[#235347]" />
            <h1 className="font-semibold text-slate-900">Confidentiality Agreement</h1>
          </div>
          <span className="text-xs text-slate-400">v{agreement?.version}</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Section Navigation */}
        <div className="mb-6 flex flex-wrap gap-2">
          {SECTION_ANCHORS.map(s => (
            <button
              key={s.id}
              onClick={() => scrollToSection(s.search)}
              className="text-xs px-3 py-1.5 bg-white border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 transition-colors"
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Agreement Text */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8 shadow-sm">
          <div
            ref={agreementRef}
            className="agreement-body"
            dangerouslySetInnerHTML={{ __html: formatAgreement(agreement?.body) }}
          />
        </div>

        {/* Signing Section */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="signing-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-6">Execution</h2>

          {/* Identity */}
          <div className="space-y-4 mb-8">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Your Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Full Legal Name *</label>
                <input
                  value={form.full_legal_name}
                  onChange={handleChange('full_legal_name')}
                  placeholder="As it appears on your identification"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20 text-sm"
                  data-testid="nda-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Email Address *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  placeholder="your@email.com"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20 text-sm"
                  data-testid="nda-email-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Company / Organisation</label>
                <input
                  value={form.company}
                  onChange={handleChange('company')}
                  placeholder="Optional"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none text-sm"
                  data-testid="nda-company-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Role / Title</label>
                <input
                  value={form.role_title}
                  onChange={handleChange('role_title')}
                  placeholder="Optional"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none text-sm"
                  data-testid="nda-role-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Country *</label>
                <input
                  value={form.country}
                  onChange={handleChange('country')}
                  placeholder="e.g. Australia"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20 text-sm"
                  data-testid="nda-country-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  placeholder="Optional"
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none text-sm"
                  data-testid="nda-phone-input"
                />
              </div>
            </div>
          </div>

          {/* Acceptance */}
          <div className="border-t border-slate-100 pt-6 mb-6">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="nda-checkbox-label">
              <input
                type="checkbox"
                checked={form.checkbox_accepted}
                onChange={handleChange('checkbox_accepted')}
                className="mt-0.5 w-5 h-5 rounded border-slate-300 text-[#235347] focus:ring-[#235347]"
                data-testid="nda-checkbox"
              />
              <span className="text-sm text-slate-700 leading-relaxed">
                I have read and agree to this Confidentiality Agreement. I understand that this creates binding obligations regarding the confidential information disclosed to me.
              </span>
            </label>
          </div>

          {/* Drawn Signature */}
          <div className="border-t border-slate-100 pt-6 mb-6">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Signature</h3>
            <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl overflow-hidden" data-testid="signature-pad-container">
              <SignatureCanvas
                ref={sigPadRef}
                penColor="#1a3a30"
                canvasProps={{
                  className: 'w-full',
                  style: { width: '100%', height: '120px' },
                }}
              />
            </div>
            <button
              onClick={clearSignature}
              className="text-xs text-slate-400 hover:text-slate-600 mt-2"
              data-testid="clear-signature-btn"
            >
              Clear signature
            </button>
          </div>

          {/* Typed Signature */}
          <div className="border-t border-slate-100 pt-6 mb-6">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Type Your Full Legal Name as Signature Confirmation</h3>
            <input
              value={form.typed_signature_name}
              onChange={handleChange('typed_signature_name')}
              placeholder="Type your full legal name exactly"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20 text-lg font-serif italic text-slate-800"
              data-testid="nda-typed-signature"
            />
          </div>

          {/* Date */}
          <div className="border-t border-slate-100 pt-6 mb-8">
            <p className="text-sm text-slate-500">
              Date of execution: <strong className="text-slate-700">{new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2" data-testid="nda-error">
              <Warning size={16} className="text-red-500 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#235347] text-white rounded-xl px-6 py-4 font-semibold hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-base"
            data-testid="sign-agreement-btn"
          >
            {submitting ? (
              <><Spinner size={20} className="animate-spin" /> Executing Agreement...</>
            ) : (
              <><ShieldCheck size={20} /> Execute Confidentiality Agreement</>
            )}
          </button>

          <p className="text-xs text-slate-400 text-center mt-4">
            By clicking above, you confirm your agreement to be bound by the terms set out in this Confidentiality Agreement.
            Electronic execution has the same legal effect as a handwritten signature.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NDAPage;
